import { SimpleRichText } from "@/components/SimpleRichText";
import { SearchFieldWrapper } from "@/components/SearchFieldWrapper";
import VideoPlayerButton from "@/components/VideoPlayerButton";
import type {
  Accordion,
  Article,
  Asset,
  Button,
  Image,
  InputField,
  InputGroup,
  Link as LinkType,
  List,
  Multilink,
  RichtextList,
  SearchField,
  SectionHeader,
  SectionTitle,
  SimpleRichText as SimpleRichTextType,
  Title,
  VideoPlayerButton as VideoPlayerButtonType,
  Video,
} from "@/generated/sb";
import { deleteAutoSlug } from "@/utils/autoSlug";
import { AutomatedArticleRichtextProps } from "@repo/ui-parkmobile/AutomatedArticleRichtext";
import { ButtonProps } from "@repo/ui-parkmobile/Button";
import { isAllowedNavatticUrl } from "@repo/utils/navattic";
import { ListProps } from "@repo/ui-parkmobile/List";
import { LogoProps } from "@repo/ui-parkmobile/Logo";
import { RichtextListProps } from "@repo/ui-parkmobile/RichtextList";
import { SectionHeaderProps } from "@repo/ui-parkmobile/SectionHeader";
import { SectionTitleProps } from "@repo/ui-parkmobile/SectionTitle";
import { TextProps } from "@repo/ui-parkmobile/Text";
import { TitleProps } from "@repo/ui-parkmobile/Title";
import { storyblokEditable } from "@storyblok/react/rsc";
import { Link } from "@repo/ui-parkmobile/Link";
import { ComponentProps, CSSProperties, default as React } from "react";
import { ISbStoryData } from "storyblok-js-client";
import { isPreviewMode } from "@/lib/previewContext";
import {
  ImageWithAspectRatioProps,
  ArticleResolverResult,
} from "@repo/ui-parkmobile/types";

// The generated Multilink type is missing properties that Storyblok sends at runtime
type MultilinkRuntime = Multilink & {
  href?: string;
  anchor?: string;
  target?: string;
  noFollow?: boolean;
};

// The generated Asset type is missing dimension/aspect ratio properties
type AssetRuntime = Asset & {
  aspect_ratio?: string;
  width?: number;
  height?: number;
};

export function multilinkResolver(
  blok?: MultilinkRuntime,
): Omit<ComponentProps<typeof Link>, "children"> | null {
  if (!blok) return null;

  if (blok.linktype === "story") {
    // Prefer the SDK-injected, live-resolved story: `blok.story` carries the target's
    // CURRENT full_slug (from the response's top-level `links` array, which Storyblok
    // resolves live regardless of the per-story `cv`). Using it first means a renamed
    // target self-heals on re-render, instead of rendering the frozen `blok.href` that
    // was captured in this referrer's content at its own publish time.
    if ((blok.story as ISbStoryData)?.full_slug) {
      const resolvedLink = storyLinkResolver(blok.story as ISbStoryData, {
        anchor: blok.anchor,
        target: blok.target,
        noFollow: blok.noFollow,
      });

      if (resolvedLink) return resolvedLink;
    }

    // Fallback for href-only links that carry no resolved story object
    // (e.g. createBulletListWithLinks synthetic links in location pages).
    if (blok.href) {
      const hrefWithoutLeadingSlash = blok.href.replace(/^\//, "");
      const cleanHref = isPreviewMode()
        ? `/live-preview/${hrefWithoutLeadingSlash}`
        : `/${deleteAutoSlug(hrefWithoutLeadingSlash)}`;
      return {
        href: blok.anchor ? `${cleanHref}#${blok.anchor}` : cleanHref,
        target: blok.target,
        noFollow: blok.noFollow,
      };
    }

    // Final fallback for story links that only have cached_url (not resolved)
    if (blok.cached_url) {
      const cachedUrlWithoutLeadingSlash = blok.cached_url.replace(/^\//, "");
      const cleanHref = isPreviewMode()
        ? `/live-preview/${cachedUrlWithoutLeadingSlash}`
        : `/${deleteAutoSlug(cachedUrlWithoutLeadingSlash)}`;
      return {
        href: blok.anchor ? `${cleanHref}#${blok.anchor}` : cleanHref,
        target: blok.target,
        noFollow: blok.noFollow,
      };
    }
    return null;
  }

  if (blok.linktype === "email") {
    if (!blok.href) return null;

    return {
      href: `mailto:${blok.href}`,
      target: blok.target,
      noFollow: blok.noFollow,
    };
  }

  if (blok.linktype === "url") {
    let urlHref = blok.cached_url || blok.url || blok.href;
    if (!urlHref) return null;

    // Don't add https:// to special protocol links (tel:, mailto:, sms:)
    if (
      !urlHref.startsWith("http://") &&
      !urlHref.startsWith("https://") &&
      !urlHref.startsWith("//") &&
      !urlHref.startsWith("tel:") &&
      !urlHref.startsWith("mailto:") &&
      !urlHref.startsWith("sms:")
    ) {
      urlHref = `https://${urlHref}`;
    }

    return {
      href: blok.anchor ? `${urlHref}#${blok.anchor}` : urlHref,
      target: blok.target,
      noFollow: blok.noFollow,
    };
  }

  if (!blok.href) return null;

  if (blok.href.startsWith("tel:")) {
    return {
      href: blok.href,
      target: blok.target,
      noFollow: blok.noFollow,
    };
  }

  return {
    href: blok.anchor ? `${blok.href}#${blok.anchor}` : blok.href,
    target: blok.target,
    noFollow: blok.noFollow,
  };
}

export function storyLinkResolver(
  story?: ISbStoryData | string,
  {
    target,
    anchor,
    noFollow,
  }: { target?: string; anchor?: string; noFollow?: boolean } = {},
): Omit<ComponentProps<typeof Link>, "children"> | null {
  if (!story || typeof story === "string") {
    return null;
  }

  const correctSlug = isPreviewMode()
    ? `/live-preview/${story.full_slug}`
    : `/${deleteAutoSlug(story.full_slug)}`;

  return {
    href: anchor ? `${correctSlug}#${anchor}` : correctSlug,
    target,
    noFollow,
  };
}

export function linkResolver(
  bloks?: LinkType[],
): Omit<ComponentProps<typeof Link>, "children"> | null {
  return multilinkResolver(bloks?.[0]?.link);
}

/** Handles both native Multilink objects and LinkStoryblok component arrays */
export function universalLinkResolver(
  field?: MultilinkRuntime | LinkType[],
): Omit<ComponentProps<typeof Link>, "children"> | null {
  if (!field) return null;
  if (Array.isArray(field)) return linkResolver(field);
  return multilinkResolver(field);
}

export function assetResolver(image?: AssetRuntime): {
  src: string;
  alt: string;
  focus?: string;
  aspectRatio?: string;
  width?: number;
  height?: number;
} | null {
  if (!image?.filename) {
    return null;
  }

  return {
    src: image.filename,
    alt: image.alt || "",
    aspectRatio: image.aspect_ratio ? String(image.aspect_ratio) : undefined,
    focus: image.focus && image.focus !== "null" ? image.focus : undefined,
    width: typeof image.width === "number" ? image.width : undefined,
    height: typeof image.height === "number" ? image.height : undefined,
  };
}

export function imageStoryblokResolver(imageBlok?: Image): {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  focus?: string;
  imageSpace?: "fill" | "fit";
  aspectRatio?: string;
  quality?: number;
  horizontalAlignment?: "left" | "center" | "right";
  verticalAlignment?: "top" | "center" | "bottom";
  horizontalAlignmentMobile?: "left" | "center" | "right";
  verticalAlignmentMobile?: "top" | "center" | "bottom";
} | null {
  if (!imageBlok?.asset?.filename) {
    return null;
  }

  const baseAsset = assetResolver(imageBlok.asset);
  if (!baseAsset || !baseAsset.src) return null;

  return {
    ...storyblokEditable(imageBlok),
    src: baseAsset.src,
    alt: baseAsset.alt,
    width: baseAsset.width,
    height: baseAsset.height,
    focus: baseAsset.focus,
    imageSpace:
      (imageBlok.imageSpace === "" ? undefined : imageBlok.imageSpace) ||
      undefined,
    aspectRatio: imageBlok.aspectRatio || baseAsset.aspectRatio,
    quality: imageBlok.quality ? parseInt(imageBlok.quality) : undefined,
    horizontalAlignment: imageBlok.horizontalAlignment || undefined,
    verticalAlignment: imageBlok.verticalAlignment || undefined,
    verticalAlignmentMobile: imageBlok.verticalAlignmentMobile || undefined,
    horizontalAlignmentMobile:
      (imageBlok.horizontalAlignmentMobile?.toLowerCase() as
        | "left"
        | "center"
        | "right") || undefined,
  };
}

// Minimal section resolver for basic functionality
export function sectionResolver(
  blok: Record<string, unknown> | null | undefined,
) {
  if (!blok) return null;

  return {
    id: blok.anchorId,
    paddingTop: blok.paddingTop,
    borderTop: blok.borderTop,
    verticalPaddings: blok.verticalPaddings,
    hideSection: blok.hideSection,
  };
}

export function titleResolver(bloks?: Title[]): TitleProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  const result = {
    ...storyblokEditable(blok),
    content: blok.content,
    level: blok.level || "p",
    fontSize: blok.fontSize,
    fontColor: blok.fontColor,
    twoToneColor: blok.twoTone || undefined,
    textWrap: blok.textWrap || "normal",
  };

  return result;
}

export function sectionTitleResolver(
  bloks?: SectionTitle[],
): SectionTitleProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  const typography = blok.typography
    ?.map((item) => {
      if (item.component === "title") {
        const titleBlok = item as Title;

        return {
          type: "title" as const,
          content: titleBlok.content,
          level: titleBlok.level || "p",
          fontSize: titleBlok.fontSize,
          fontColor: titleBlok.fontColor,
          twoToneColor: titleBlok.twoTone || undefined,
          textWrap: titleBlok.textWrap || "normal",
          className: undefined,
        };
      }
      if (item.component === "simpleRichText") {
        const richTextBlok = item as SimpleRichTextType;
        return {
          type: "text" as const,
          content: <SimpleRichText blok={richTextBlok} />,
          size: richTextBlok.size,
          fontColor: richTextBlok.fontColor,
          className: undefined,
        };
      }
      if (item.component === "list") {
        const listBlok = item as List;
        const items =
          listBlok.items
            ?.map((listItem) => {
              if (listItem.component === "listItem") {
                const iconData = listItem.icon?.[0]
                  ? imageStoryblokResolver(listItem.icon[0])
                  : undefined;
                return {
                  type: "listItem" as const,
                  text: listItem.text || "",
                  icon: iconData,
                  labelColor: listItem.labelColor || undefined,
                };
              }
              if (listItem.component === "listLink") {
                const iconData = listItem.icon?.[0]
                  ? imageStoryblokResolver(listItem.icon[0])
                  : undefined;
                const link = linkResolver(listItem.link);
                return {
                  type: "listLink" as const,
                  text: listItem.text || "",
                  icon: iconData,
                  href: link?.href ? String(link.href) : undefined,
                  target: link?.target,
                  noFollow: link?.noFollow,
                };
              }
              if (listItem.component === "accordionItem") {
                return {
                  type: "accordionItem" as const,
                  title: listItem.title || "",
                  text: (listItem.text || [])
                    .map((textItem) => {
                      if (textItem.component === "simpleRichText") {
                        const richTextBlok = textItem as SimpleRichTextType;
                        return {
                          content: <SimpleRichText blok={richTextBlok} />,
                          size: richTextBlok.size,
                          fontColor: richTextBlok.fontColor,
                        };
                      }
                      return null;
                    })
                    .filter((t): t is NonNullable<typeof t> => t !== null),
                  backgroundColor: listItem.backgroundColor,
                };
              }
              return null;
            })
            .filter((i): i is NonNullable<typeof i> => i !== null) || [];

        return {
          type: "list" as const,
          items,
          className: undefined,
        };
      }
      if (item.component === "accordion") {
        const accordionBlok = item as Accordion;
        const items =
          accordionBlok.items
            ?.map((accordionItem) => ({
              title: accordionItem.title || "",
              text:
                accordionItem.text
                  ?.map((textBlok) => textResolver([textBlok]))
                  .filter((t): t is NonNullable<typeof t> => t !== null) || [],
              backgroundColor: accordionItem.backgroundColor,
            }))
            .filter((item) => item.title) || [];

        return {
          type: "accordion" as const,
          items,
          className: undefined,
        };
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const buttons = blok.buttons
    ?.map((buttonBlok: Button | SearchField | VideoPlayerButtonType) => {
      if (buttonBlok.component === "searchField") {
        const searchFieldBlok = buttonBlok as SearchField;
        return {
          type: "searchField" as const,
          placeholder: searchFieldBlok.placeholder,
          component: SearchFieldWrapper,
          analyticsEvent: searchFieldBlok.analyticsEvent || undefined,
        };
      }

      if (buttonBlok.component === "videoPlayerButton") {
        const videoButtonBlok = buttonBlok as VideoPlayerButtonType;
        return {
          type: "videoPlayerButton" as const,
          component: () => <VideoPlayerButton blok={videoButtonBlok} />,
        };
      }

      return buttonResolver(buttonBlok);
    })
    // buttonResolver returns null for unusable buttons (e.g. type=navattic
    // without a valid URL); injectButtonAnalytics and SectionTitle expect items.
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    ...storyblokEditable(blok),
    tagText: blok.tagText,
    typography: typography?.length
      ? (typography as SectionTitleProps["typography"])
      : undefined,
    buttons: buttons?.length
      ? (buttons as SectionTitleProps["buttons"])
      : undefined,
    layout: blok.layout || "left",
  };
}

export function textResolver(
  bloks?: SimpleRichTextType[],
  { color, opacity }: { color?: TextProps["color"]; opacity?: number } = {},
): TextProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  // Map Storyblok size to Text fontSize/variant
  const sizeToFontSize: Record<string, TextProps["fontSize"]> = {
    small: "caption",
    medium: "body",
    large: "bodyLarge",
  };

  return {
    children: <SimpleRichText blok={blok} />,
    fontSize: blok.size ? sizeToFontSize[blok.size] : undefined,
    ...(color ? { color } : {}),
    ...(opacity ? { opacity } : {}),
  };
}

export function buttonResolver(blok: Button): ButtonProps | null {
  if (!blok) {
    return null;
  }

  const link = universalLinkResolver(blok.link);

  const props: ButtonProps = {
    ...storyblokEditable(blok),
    text: blok.text || "",
    variant: blok.variant || "primary",
    href: link?.href ? String(link.href) : undefined,
    target: link?.target,
    noFollow: link?.noFollow,
    analyticsEvent: (blok.analyticsEvent ||
      undefined) as ButtonProps["analyticsEvent"],
  };

  // Storyblok `button.type === "navattic"`: open a Navattic interactive demo in
  // Navattic's lightbox on this page. This resolver runs inside Server
  // Components (everything rendered through StoryblokServerComponent), so the
  // result must stay serializable: no onClick here. `Button` turns
  // `navatticUrl` into the client-side `NavatticDemoButton`, which owns the
  // click; section-level analytics (`injectButtonAnalytics`) still wrap onClick
  // and run first. An empty/missing type means "regular".
  if (blok.type === "navattic") {
    const navatticUrl: unknown = blok.navatticUrl;

    if (!isAllowedNavatticUrl(navatticUrl)) {
      console.warn(
        "buttonResolver: type=navattic but no valid Navattic demo URL",
        { text: blok.text, url: navatticUrl },
      );
      // Editors see a disabled button that says what is missing; production
      // drops the CTA. `_editable` exists only on draft content and, unlike the
      // server-only isPreviewMode(), is the same in client components.
      if (blok._editable || isPreviewMode()) {
        // Both `text` (SectionTitle renders item.text) and `children` (spread consumers).
        const label = `${blok.text || "Button"} (missing Navattic URL)`;
        return {
          ...props,
          href: undefined,
          target: undefined,
          noFollow: undefined,
          disabled: true,
          text: label,
          children: label,
        };
      }
      return null;
    }

    const title =
      (typeof blok.navatticTitle === "string" && blok.navatticTitle) ||
      blok.text ||
      undefined;

    // Optional mobile flow: only a valid URL is passed on, so the client
    // falls back to the desktop demo. Editors see why it was ignored.
    const mobileUrl: unknown =
      typeof blok.navatticMobileUrl === "string"
        ? blok.navatticMobileUrl.trim()
        : undefined;
    const hasMobileUrl = isAllowedNavatticUrl(mobileUrl);
    const hasInvalidMobileUrl =
      Boolean(mobileUrl) &&
      !hasMobileUrl &&
      Boolean(blok._editable || isPreviewMode());
    const text = hasInvalidMobileUrl
      ? `${blok.text || "Button"} (invalid mobile Navattic URL)`
      : blok.text;

    // No href on purpose: Button then renders the client trigger, not <Link>.
    return {
      ...props,
      text: text || "",
      href: undefined,
      target: undefined,
      noFollow: undefined,
      navatticUrl,
      navatticTitle: title,
      navatticMobileUrl: hasMobileUrl ? mobileUrl : undefined,
      children: text,
    };
  }

  if (link) {
    if (typeof link.href === "string" && link.href.startsWith("#")) {
      return {
        ...props,
        href: link.href,
        target: link.target,
        children: blok.text,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          const targetId = link.href?.toString().substring(1);
          const targetElement = document.getElementById(targetId || "");
          if (targetElement) {
            const navbar = document.querySelector(
              "[data-navbar]",
            ) as HTMLElement;
            const navbarHeight =
              navbar?.offsetHeight || (window.innerWidth >= 768 ? 90 : 60);

            const elementRect = targetElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const offsetPosition = absoluteElementTop - navbarHeight;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        },
      };
    }

    return {
      ...props,
      href: link.href ? String(link.href) : undefined,
      children: blok.text,
    };
  }

  return {
    ...props,
    children: blok.text,
  };
}

export function listResolver(bloks?: List[]): ListProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  const items = blok.items
    ?.map((listItem) => {
      if (listItem.component === "listItem") {
        const iconData = listItem.icon?.[0]
          ? imageStoryblokResolver(listItem.icon[0])
          : undefined;
        return {
          type: "listItem" as const,
          text: listItem.text || "",
          icon: iconData,
          labelColor: listItem.labelColor || undefined,
        };
      }
      if (listItem.component === "listLink") {
        const iconData = listItem.icon?.[0]
          ? imageStoryblokResolver(listItem.icon[0])
          : undefined;
        const link = linkResolver(listItem.link);
        return {
          type: "listLink" as const,
          text: listItem.text || "",
          icon: iconData,
          href: link?.href ? String(link.href) : undefined,
          target: link?.target,
          noFollow: link?.noFollow,
        };
      }
      if (listItem.component === "accordionItem") {
        return {
          type: "accordionItem" as const,
          title: listItem.title || "",
          text: (listItem.text || [])
            .map((textItem) => {
              if (textItem.component === "simpleRichText") {
                const richTextBlok = textItem as SimpleRichTextType;
                return {
                  content: <SimpleRichText blok={richTextBlok} />,
                  size: richTextBlok.size,
                  fontColor: richTextBlok.fontColor,
                };
              }
              return null;
            })
            .filter((t): t is NonNullable<typeof t> => t !== null),
          backgroundColor: listItem.backgroundColor || undefined,
        };
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    ...storyblokEditable(blok),
    items: (items || []) as ListProps["items"],
  };
}

export function logoResolver(blok: { logo?: Image[] }): LogoProps | null {
  if (!blok || !blok.logo) {
    return null;
  }

  const logoAsset = blok.logo[0]
    ? imageStoryblokResolver(blok.logo[0])
    : undefined;

  return {
    image: logoAsset,
  };
}

export function sectionHeaderResolver(
  bloks?: SectionHeader[],
): SectionHeaderProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  return {
    ...storyblokEditable(blok),
    title: titleResolver(blok.title),
    text: textResolver(blok.text),
    buttons: blok.buttons?.map(buttonResolver).filter(Boolean) as
      | ButtonProps[]
      | undefined,
    layout: blok.layout || "left",
  };
}

export function imageResolver(
  bloks?: Image[],
  { isStoryblokEditable }: { isStoryblokEditable?: boolean } = {},
): ImageWithAspectRatioProps | null {
  const blok = bloks?.[0];

  if (!blok) {
    return null;
  }

  const imageData = imageStoryblokResolver(blok);
  if (!imageData) return null;

  return {
    ...(isStoryblokEditable === false ? {} : storyblokEditable(blok)),
    ...imageData,
  };
}

export function contactFormFieldsResolver(
  bloks?: (InputField | InputGroup)[],
): unknown[] | null {
  if (!bloks) return null;

  return bloks
    .filter((blok) => {
      if (blok.component === "inputGroup") {
        return blok.items && blok.items.length > 0;
      }
      return true;
    })
    .map((blok) => {
      if (blok.component === "inputGroup") {
        return {
          ...storyblokEditable(blok),
          component: "inputGroup",
          _uid: blok._uid,
          title: blok.title,
          showAddMoreButton: blok.showAddMoreButton,
          items:
            blok.items?.map((item) => ({
              ...storyblokEditable(item),
              label: item.label,
              name: item.name,
              type: item.type,
              placeholder: item.placeholder,
              required: item.required,
              requiredErrorText: item.requiredErrorText,
              validations: item.validations,
              options: item.options || [],
            })) || [],
        };
      } else {
        return {
          ...storyblokEditable(blok),
          label: blok.label,
          name: blok.name,
          type: blok.type,
          placeholder: blok.placeholder,
          required: blok.required,
          requiredErrorText: blok.requiredErrorText,
          validations: blok.validations,
          options: blok.options || [],
        };
      }
    });
}

/**
 * Process richtext content to fix internal story links
 * Removes "parkmobile" prefix and ensures proper absolute paths
 */
function processRichtextLinks(content: unknown): unknown {
  if (!content || typeof content !== "object") {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((item) => processRichtextLinks(item));
  }

  const obj = content as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(obj)) {
    if (key === "marks" && Array.isArray(obj[key])) {
      // Process marks array to fix link hrefs
      result[key] = (obj[key] as Array<Record<string, unknown>>).map((mark) => {
        if (mark.type === "link" && mark.attrs) {
          const attrs = mark.attrs as Record<string, unknown>;
          // Prefer the live-resolved story slug: the SDK injects `attrs.story` with the
          // target's CURRENT full_slug, so a renamed target self-heals on re-render.
          // Fall back to the frozen `attrs.href` rewrite below when no story is resolved.
          const story = attrs.story as { full_slug?: string } | undefined;
          if (attrs.linktype === "story" && story?.full_slug) {
            const cleanHref = isPreviewMode()
              ? `/live-preview/${deleteAutoSlug(story.full_slug)}`
              : `/${deleteAutoSlug(story.full_slug)}`;
            return {
              ...mark,
              attrs: {
                ...attrs,
                href: cleanHref,
              },
            };
          }
          const href = attrs.href as string | undefined;
          if (href && typeof href === "string" && !href.startsWith("http")) {
            // Check if this is an internal link that needs fixing
            const needsProcessing =
              attrs.linktype === "story" ||
              href.startsWith("parkmobile/") ||
              href.startsWith("/parkmobile/");

            if (needsProcessing) {
              const hrefWithoutLeadingSlash = href.replace(/^\//, "");
              const cleanHref = isPreviewMode()
                ? `/live-preview/${deleteAutoSlug(hrefWithoutLeadingSlash)}`
                : `/${deleteAutoSlug(hrefWithoutLeadingSlash)}`;
              return {
                ...mark,
                attrs: {
                  ...attrs,
                  href: cleanHref,
                },
              };
            }
          }
        }
        return mark;
      });
    } else {
      result[key] = processRichtextLinks(obj[key]);
    }
  }

  return result;
}

export function automatedArticleRichtextResolver(blok?: {
  richtext?: unknown;
}): AutomatedArticleRichtextProps | null {
  if (!blok?.richtext) {
    return null;
  }

  // Process richtext to fix internal links
  const processedRichtext = processRichtextLinks(blok.richtext);

  return {
    richtext: processedRichtext as AutomatedArticleRichtextProps["richtext"],
  };
}

export const articleResolver = ({
  article,
  imageAspectRatio = "16/9",
  disableDescription = false,
}: {
  article:
    | ISbStoryData<Article>
    | (Partial<ISbStoryData<Article>> & {
        disableDescription?: boolean;
        buttons?: Button[];
      });
  imageAspectRatio?: string;
  disableDescription?: boolean;
}): ArticleResolverResult | null => {
  if (!article) return null;

  let categoryName = "";
  if (article?.content?.type) {
    if (typeof article.content.type === "string") {
      categoryName = "";
    } else if (
      article.content.type &&
      typeof article.content.type === "object" &&
      "name" in article.content.type
    ) {
      categoryName = (article.content.type as any).name || "";
    }
  }

  const resolvedOgImage = article?.content?.ogImage
    ? assetResolver(article.content.ogImage)
    : null;
  const articleImage = resolvedOgImage
    ? { ...resolvedOgImage, aspectRatio: imageAspectRatio }
    : undefined;

  const resolvedTitle = article?.content?.seoTitle || article?.name || "";

  const articleDescription =
    !disableDescription &&
    !(article as any).disableDescription &&
    article.content?.seoDescription
      ? {
          children: article.content.seoDescription,
        }
      : undefined;

  const resolvedButtons = (article as any).buttons
    ?.map((button: Button) => buttonResolver(button))
    .filter(Boolean) as ButtonProps[] | undefined;

  const result: ArticleResolverResult & Record<string, any> = {
    category: categoryName,
    title: resolvedTitle,
    description: articleDescription,
    image: articleImage,
    buttons: resolvedButtons,
  };

  if (article && "_uid" in article) {
    Object.assign(result, storyblokEditable(article as any));
  }

  return result;
};

export function richtextListResolver(
  blok?: RichtextList,
): Omit<RichtextListProps, "children"> | null {
  if (!blok) {
    return null;
  }

  return {
    ...storyblokEditable(blok),
    columnsAmount: blok.columnsAmount || "one",
  };
}

export interface VideoResolverResult {
  src: string;
  alt: string;
  aspectRatio?: string;
  quality?: number;
}

export function videoResolver(
  blok?: Video,
  { aspectRatio, quality }: { aspectRatio?: string; quality?: number } = {},
): VideoResolverResult | null {
  // Handle Wistia videos with url field
  if (blok?.url) {
    return {
      src: blok.url,
      alt: "",
      aspectRatio: aspectRatio || "3/2",
      quality,
    };
  }

  // Handle regular video assets with content field
  if (!blok?.content) {
    return null;
  }

  const asset = assetResolver(blok.content);
  if (!asset || !asset.src) return null;

  return {
    src: asset.src,
    alt: asset.alt,
    aspectRatio: aspectRatio || asset.aspectRatio,
    quality,
  };
}
